// Veřejná stránka pro zobrazení souborů zákazníka (read-only, bez auth).
// Aktivuje se přes URL `/public-files/{token}` – routing zajišťuje app.js.
//
// Z backendu si vytahuje data přes GET /api/public/files/:token a podle
// MIME typu poskládá seznam souborů s akcemi (Náhled, Stáhnout). Stránka
// nemá menu ani footer a layoutem se drží stejné estetiky jako public-offer.js.

import logoUrl from 'url:../images/logo-icon.svg';
import { icon, getFileIconSvg } from './icons.js';

// 7 dní v ms – po této době badge "Nové" mizí
const NEW_BADGE_MS = 7 * 24 * 60 * 60 * 1000;

// Přípony obrázků pro lightbox náhled
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'];

// Přípony archivů (zobrazujeme ikonu, náhled nedělá smysl)
const ARCHIVE_EXT = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];

function getExt(name) {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}

function isImage(file) {
  if (!file) return false;
  if (file.mimeType && file.mimeType.startsWith('image/')) return true;
  return IMAGE_EXT.includes(getExt(file.originalName));
}

function isPdf(file) {
  if (!file) return false;
  if (file.mimeType === 'application/pdf') return true;
  return getExt(file.originalName) === 'pdf';
}

function isArchive(file) {
  return ARCHIVE_EXT.includes(getExt(file.originalName));
}

export class PublicFilesManager {
  constructor() {
    this.token = null;
    this.data = null;
  }

  async loadFilesByToken(token) {
    this.token = token;

    const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:1234/api'
      : '/api';

    try {
      const response = await fetch(`${API_BASE_URL}/public/files/${token}`);
      if (!response.ok) {
        if (response.status === 404) {
          return this.showError('Odkaz nebyl nalezen nebo již není platný.');
        }
        return this.showError('Při načítání souborů došlo k chybě. Zkuste to prosím za chvíli znovu.');
      }
      const json = await response.json();
      if (!json.success || !json.data) {
        return this.showError('Neplatná odpověď serveru.');
      }
      this.data = json.data;
      this.renderPage();
    } catch (err) {
      console.error('Chyba při načítání public files:', err);
      this.showError('Stránka se nepodařilo načíst. Zkontrolujte připojení k internetu.');
    }
  }

  // ===== Rendering =====

  showError(message) {
    const container = document.getElementById('public-files-content');
    if (!container) return;
    container.innerHTML = `
      <div class="max-w-xl mx-auto px-4 py-16">
        <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div class="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
            ${icon('exclamation-triangle', 'w-6 h-6 text-red-500')}
          </div>
          <h1 class="text-lg font-semibold text-gray-900 mb-1">Odkaz není dostupný</h1>
          <p class="text-sm text-gray-600">${this.escape(message)}</p>
        </div>
      </div>
    `;
  }

  renderPage() {
    const container = document.getElementById('public-files-content');
    if (!container) return;
    const { customer, files } = this.data;

    // Zabraň crawlerům indexovat tuto osobní stránku a sledovat download/preview
    // linky (každý crawl / unfurl = 1 operace na Vercel Blob).
    this.applyRobotsMeta();

    const filesSorted = (files || []).slice().sort(
      (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );

    const totalSize = filesSorted.reduce((sum, f) => sum + (f.size || 0), 0);

    const empty = filesSorted.length === 0;

    container.innerHTML = `
      <div class="min-h-screen bg-gray-50">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

          <header class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-6 border-b border-gray-200 mb-8">
            <div class="flex items-center gap-3">
              <img src="${logoUrl}" alt="Tiskárna Tria, s.r.o." class="h-10 w-auto">
              <div class="hidden sm:block leading-tight">
                <div class="text-sm font-semibold text-gray-900">Tiskárna Tria, s.r.o.</div>
                <div class="text-xs text-gray-500">Sdílené soubory</div>
              </div>
            </div>
            <div class="text-left sm:text-right">
              <div class="text-xs uppercase tracking-wide text-gray-500">Soubory pro</div>
              <div class="text-lg sm:text-xl font-semibold text-gray-900 break-words">${this.escape(customer?.name || '')}</div>
            </div>
          </header>

          ${empty ? this.renderEmpty() : this.renderFilesList(filesSorted, totalSize)}

          <div class="public-files-disclaimer mt-12 pt-6 border-t border-gray-100 text-center text-xs text-gray-400">
            Stránka je generována pro vás osobně.
            Pro otázky nás kontaktujte na <a href="mailto:info@tria.cz" class="text-blue-600 hover:underline">info@tria.cz</a>.
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  renderEmpty() {
    return `
      <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 sm:p-14 text-center">
        <div class="mx-auto w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mb-4">
          ${icon('folder', 'w-7 h-7 text-gray-400')}
        </div>
        <h2 class="text-base font-semibold text-gray-900 mb-1">Žádné soubory</h2>
        <p class="text-sm text-gray-500">Zatím vám nebyly žádné soubory přiřazeny. Až přibydou, najdete je tady.</p>
      </div>
    `;
  }

  renderFilesList(files, totalSize) {
    const rows = files.map((f) => this.renderFileRow(f)).join('');
    return `
      <div class="mb-3 flex items-center justify-between text-xs text-gray-500">
        <span><span class="font-semibold text-gray-900">${files.length}</span> ${this.plural(files.length, 'soubor', 'soubory', 'souborů')} · ${this.formatSize(totalSize)} celkem</span>
        <span class="hidden sm:inline">Nejnovější nahoře</span>
      </div>
      <ul class="space-y-3">
        ${rows}
      </ul>
    `;
  }

  renderFileRow(file) {
    const ext = getExt(file.originalName);
    const iconSvg = this.getFileIconSvg(file.mimeType, file.originalName);
    const isNew = this.isNew(file.uploadedAt);
    const canPreview = isImage(file) || isPdf(file);

    return `
      <li class="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300 transition-all">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
          <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-blue-100/60 flex items-center justify-center text-blue-600">
            ${iconSvg}
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 break-all" title="${this.escape(file.originalName)}">
                ${this.escape(file.originalName)}
              </h3>
              ${isNew ? `
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/60">
                  Nové
                </span>` : ''}
              ${ext ? `
                <span class="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-gray-100 text-gray-600">
                  ${this.escape(ext)}
                </span>` : ''}
            </div>
            ${file.description ? `
              <p class="text-sm text-gray-600 mt-0.5 break-words">${this.escape(file.description)}</p>` : ''}
            <div class="mt-1 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
              <span>${this.formatDateTime(file.uploadedAt)}</span>
              <span class="text-gray-300">·</span>
              <span class="tabular-nums">${this.formatSize(file.size)}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 sm:flex-shrink-0">
            ${canPreview ? `
              <button type="button" data-action="preview" data-id="${this.escape(file.id)}"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                ${icon('eye', 'w-4 h-4')}
                Náhled
              </button>` : ''}
            <a href="${this.escape(file.downloadUrl)}"
              rel="nofollow noopener"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
              data-action="download"
              data-id="${this.escape(file.id)}">
              ${icon('arrow-down-tray', 'w-4 h-4')}
              Stáhnout
            </a>
          </div>
        </div>
      </li>
    `;
  }

  bindEvents() {
    const container = document.getElementById('public-files-content');
    if (!container) return;
    container.querySelectorAll('[data-action="preview"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openPreview(btn.dataset.id));
    });
  }

  // ===== Preview (lightbox / iframe modal) =====

  openPreview(fileId) {
    const file = (this.data?.files || []).find((f) => f.id === fileId);
    if (!file) return;

    if (isImage(file)) {
      this.openImageLightbox(file);
    } else if (isPdf(file)) {
      this.openPdfModal(file);
    }
  }

  openImageLightbox(file) {
    this.closePreview();
    const overlay = document.createElement('div');
    overlay.id = 'public-files-preview';
    overlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 sm:p-8';
    overlay.innerHTML = `
      <button type="button" data-close="1" aria-label="Zavřít"
        class="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
        ${icon('x-mark', 'w-5 h-5')}
      </button>
      <div class="max-w-full max-h-full flex flex-col items-center gap-4">
        <img src="${this.escape(file.previewUrl)}" alt="${this.escape(file.originalName)}"
          class="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          loading="eager">
        <div class="flex items-center gap-3 text-white">
          <span class="text-sm font-medium truncate max-w-xs sm:max-w-md" title="${this.escape(file.originalName)}">${this.escape(file.originalName)}</span>
          <a href="${this.escape(file.downloadUrl)}"
            rel="nofollow noopener"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-white/15 hover:bg-white/25 text-white transition-colors">
            ${icon('arrow-down-tray', 'w-4 h-4')}
            Stáhnout
          </a>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    this.attachPreviewClose(overlay);
  }

  openPdfModal(file) {
    this.closePreview();
    const overlay = document.createElement('div');
    overlay.id = 'public-files-preview';
    overlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-6';
    overlay.innerHTML = `
      <div class="bg-white w-full max-w-5xl h-[92vh] sm:h-[88vh] rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <div class="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div class="min-w-0 flex items-center gap-2">
            ${icon('document-text', 'w-5 h-5 text-red-500 flex-shrink-0')}
            <span class="text-sm font-medium text-gray-900 truncate" title="${this.escape(file.originalName)}">${this.escape(file.originalName)}</span>
          </div>
          <div class="flex items-center gap-2">
            <a href="${this.escape(file.downloadUrl)}"
              rel="nofollow noopener"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors">
              ${icon('arrow-down-tray', 'w-4 h-4')}
              Stáhnout
            </a>
            <button type="button" data-close="1" aria-label="Zavřít"
              class="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-200 transition-colors">
              ${icon('x-mark', 'w-5 h-5')}
            </button>
          </div>
        </div>
        <iframe src="${this.escape(file.previewUrl)}#zoom=page-fit"
          title="${this.escape(file.originalName)}"
          class="flex-1 w-full border-0 bg-gray-100">
        </iframe>
      </div>
    `;
    document.body.appendChild(overlay);
    this.attachPreviewClose(overlay);
  }

  attachPreviewClose(overlay) {
    const close = () => this.closePreview();
    overlay.querySelectorAll('[data-close="1"]').forEach((el) => el.addEventListener('click', close));
    overlay.addEventListener('click', (e) => {
      // Klik mimo "kartu" (přímo na overlay)
      if (e.target === overlay) close();
    });
    const onKey = (e) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    overlay._cleanup = () => document.removeEventListener('keydown', onKey);
  }

  closePreview() {
    const overlay = document.getElementById('public-files-preview');
    if (!overlay) return;
    if (typeof overlay._cleanup === 'function') overlay._cleanup();
    overlay.remove();
  }

  // ===== Utility =====

  applyRobotsMeta() {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
  }

  isNew(uploadedAt) {
    if (!uploadedAt) return false;
    const ts = new Date(uploadedAt).getTime();
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < NEW_BADGE_MS;
  }

  formatSize(bytes) {
    const b = Number(bytes) || 0;
    if (b === 0) return '0 B';
    const units = ['B', 'kB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
    const value = b / Math.pow(1024, i);
    const formatted = value >= 100 ? value.toFixed(0) : value.toFixed(1);
    return `${formatted.replace('.', ',')} ${units[i]}`;
  }

  formatDateTime(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (_) {
      return iso;
    }
  }

  plural(count, one, few, many) {
    const n = Math.abs(count);
    if (n === 1) return one;
    if (n >= 2 && n <= 4) return few;
    return many;
  }

  getFileIconSvg(mimeType, filename) {
    return getFileIconSvg(mimeType, filename, 'w-6 h-6');
  }

  escape(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
